import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useIsSwapMode, usePhotoSwapping } from "./use-photo-swapping";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useShowDesktopLayout } from "../../../hooks/useShowdesktopLayout";
import { useAppContext } from "../../../hooks/useAppContext";
import { useEditor } from "../../../hooks/useEditor";
import { useIsPreviewMode } from "../../../hooks/useIsPreviewMode";
import { useIsTextEditMode } from "../../../hooks/useIsTextEditMode";
import { useIsTransformMode } from "../../../hooks/useIsTransformMode";
import { transformPoint } from "../../../transform";
import { useGuidelineOverlay } from "../../guideline-overlay/use-guideline-overlay";
import { TransformType, useSnapping } from "./use-snapping";

interface UseCanMoveZoneOptions {
	zone: eXpress.core.IZone | undefined;
	isEnabled?: boolean;
	isZoneResizing?: boolean;
}

export function useCanMoveZone({
	zone,
	isEnabled = true,
	isZoneResizing = false,
}: UseCanMoveZoneOptions) {
	const [isPreviewMode] = useIsPreviewMode();
	const [isTransformMode] = useIsTransformMode();
	const [isPhotoSwapping] = usePhotoSwapping(undefined);
	const { isSwapModeActive } = useIsSwapMode();
	const isTextEditMode = useIsTextEditMode();
	const showDesktopLayout = useShowDesktopLayout();

	return !!(
		isEnabled &&
		!isPreviewMode &&
		!isTransformMode &&
		!isPhotoSwapping &&
		!isSwapModeActive &&
		!isZoneResizing &&
		zone?.canChangeTransform() &&
		(showDesktopLayout || !isTextEditMode)
	);
}

/** Allowed region in DIP */
export function getAllowedZoneRegion(selectedZone: eXpress.core.IZone) {
	return selectedZone.spread.pageZones
		.filter((p) => p.canChangeDesign())
		.map((p) => p.spreadOuterBounds)
		.reduce<eXpress.math.Rect | undefined>(
			(combined, p) => (combined ? combined.union(p) : p),
			undefined,
		);
}

/** Disallowed region in DIP */
export function getDisallowedZoneRegion(selectedZone: eXpress.core.IZone) {
	return selectedZone.spread.pageZones
		.filter((p) => !p.canChangeDesign())
		.map((p) => p.spreadOuterBounds)
		.reduce<eXpress.math.Rect | undefined>(
			(combined, p) => (combined ? combined.union(p) : p),
			undefined,
		);
}

/**
 * Minimum distance to move a zone into the allowed region
 * TODO: Add margin to be inside, instead of just touch?
 */
function moveTowardAllowedRegion(
	zoneParallelogram: eXpress.math.Frame,
	allowedRegion: eXpress.math.Rect,
): eXpress.math.Frame {
	let adjustedFrame = zoneParallelogram;

	// Is zone outside allowed region?
	// zone must overlap allowed region to be inside of it
	if (!zoneParallelogram.overlaps(allowedRegion.toFrame())) {
		// Move the zone by the closest path to the allowed region.
		const zoneEdges = zoneParallelogram.edges();
		const allowedRegionEdges = allowedRegion.edges();

		const closesZoneDisplacementToAllowedRegion = zoneEdges
			// Minimum displacements from zone edge to an allowed region side
			// this is the minimum distance to move a zone to touch the allowed region
			.flatMap((zoneEdge) =>
				allowedRegionEdges.map((region) => zoneEdge.displacementTo(region)),
			)
			.sort((v1, v2) => v1.length() - v2.length()) // find the closest displacement
			.first();

		if (closesZoneDisplacementToAllowedRegion) {
			adjustedFrame = zoneParallelogram.translate(closesZoneDisplacementToAllowedRegion);
		}
	}

	return adjustedFrame;
}

/** Shared state */
const zoneMovingState = proxy({
	isZoneMoving: false,
	actions: {
		startZoneMove() {
			zoneMovingState.isZoneMoving = true;
		},
		endZoneMove() {
			zoneMovingState.isZoneMoving = false;
		},
	},
});

interface IZoneMoveState {
	selectedZone: eXpress.core.ITransformableZone;
	/** Distance from top/left of zone to pointer (in DIP) */
	touchOffsetInDip?: eXpress.math.Vector;
	/** The zone transform at the start */
	zoneTransform: eXpress.core.IZoneTransform;
	/** Remember our command row Id to know if we can finalize/undo it at the end */
	undoRowId: string | undefined;
	/** The region were we can place our zone */
	allowedRegion: eXpress.math.Rect | undefined;
	/** The region were no zone can be placed */
	disallowedRegion: eXpress.math.Rect | undefined;
	/** Log once during drag */
	hasLoggedDisallowedRegionError?: boolean;
}

/**
 * Logic to move a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function useZoneMove() {
	/** The start location of dragging (In DIP so it is independent of scale panning) */
	const zoneMoveRef = useRef<IZoneMoveState | undefined>(undefined);

	const { getScreenToSpreadTransform } = usePreviewContext();
	const { commandManager, client } = useEditor();

	const { googleAnalytics } = useAppContext();
	const { isZoneMoving } = useSnapshot(zoneMovingState);

	const snapTransform = useSnapping();
	const [lines, useLines] = useGuidelineOverlay();

	const startFromCurrentTransform = useCallback(
		function startZoneMovingFromCurrentTransform(
			selectedZone: eXpress.core.ITransformableZone,
		) {
			zoneMovingState.actions.startZoneMove();
			useLines.setIsZoneMoving(true);
			useLines.setTransformType(TransformType.Move);
			zoneMoveRef.current = {
				selectedZone,
				zoneTransform: selectedZone.transform(),
				undoRowId: undefined,
				allowedRegion: getAllowedZoneRegion(selectedZone),
				disallowedRegion: getDisallowedZoneRegion(selectedZone),
			};
		},
		[useLines],
	);

	const updateWithDisplacement = useCallback(
		function updateZoneMoveWithDisplacement(
			displacementInDip: eXpress.math.Vector,
			{ shouldSnap = true }: { shouldSnap?: boolean } = {},
		) {
			const {
				zoneTransform,
				hasLoggedDisallowedRegionError,
				selectedZone,
				allowedRegion,
				disallowedRegion,
			} = zoneMoveRef.current || {};
			if (!zoneTransform || !selectedZone || !allowedRegion) return;

			useLines.setIsZoneMoving(true);
			// Move to the new position
			let newZoneTransform = zoneTransform.clone() as eXpress.core.ZoneTransform;
			let zoneAsFrame = zoneTransform
				.getParallelogram() // Get Frame representing the zone (with rotation)
				.translate(displacementInDip);

			// Ensure we stay in allowed region
			zoneAsFrame = moveTowardAllowedRegion(zoneAsFrame, allowedRegion);

			// TODO: prevent moving to a 'disallowed' region
			// Use case: a multi-page product where one page is fixed
			// We could skip if the update if the new zone position is on a disallowed zone, but then we:
			// - cannot be moved from a disallowed zone to an allowed zone
			// - will not always be at the DIP limit of the maximum/minimum size
			if (
				!hasLoggedDisallowedRegionError &&
				disallowedRegion &&
				zoneAsFrame.overlaps(disallowedRegion.toFrame())
			) {
				const error = new eXpress.NotSupportedException(
					`Moving a zone on a multipage spread '${selectedZone.spread?.toString()}' where a page is fixed.`,
				);
				client.logErrorAsync(error, false);
				if (zoneMoveRef.current) zoneMoveRef.current.hasLoggedDisallowedRegionError = true;
			}

			// Update zoneTransform with the new position
			newZoneTransform.setPosition(zoneAsFrame.getTranslation());

			if (shouldSnap && snapTransform) {
				const newTransform = snapTransform(newZoneTransform, lines, TransformType.Move);
				if (!newTransform.isSimilarTo(newZoneTransform)) {
					newZoneTransform.left = newTransform.left;
					newZoneTransform.top = newTransform.top;

					newZoneTransform = newZoneTransform.snapToDips();
				}
			}
			newZoneTransform.left = Math.round(newZoneTransform.left);
			newZoneTransform.top = Math.round(newZoneTransform.top);
			newZoneTransform.width = Math.round(newZoneTransform.width);
			newZoneTransform.height = Math.round(newZoneTransform.height);

			const command = eXpress.core.commands.createTranslateZoneCommand(
				selectedZone,
				newZoneTransform,
				true,
			);
			// When dragging, we can receive events faster than the screen can show (faster than refresh rate of the screen)
			// I tested with RAF throttling but that caused a noticable delay when dragging, did not use RAF throttling
			commandManager.exec(command);

			if (zoneMoveRef.current) zoneMoveRef.current.undoRowId = commandManager.getRowId();
		},
		[useLines, client, snapTransform, lines, commandManager],
	);

	const start = useCallback(
		function startZoneMoving({
			selectedZone,
			pointOnScreen,
		}: {
			selectedZone: eXpress.core.ITransformableZone;
			pointOnScreen: eXpress.math.Point;
		}) {
			// Get DIP coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(selectedZone.spread, undefined);
			const touchDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;

			if (!touchDip) {
				zoneMoveRef.current = undefined;
				return;
			}

			startFromCurrentTransform(selectedZone);
			if (zoneMoveRef.current) {
				zoneMoveRef.current.touchOffsetInDip = touchDip.from(selectedZone);
			}
		},
		[getScreenToSpreadTransform, startFromCurrentTransform],
	);

	const update = useCallback(
		function zoneMoving(pointOnScreen: eXpress.math.Point) {
			const { touchOffsetInDip, zoneTransform, selectedZone } = zoneMoveRef.current || {};
			if (!touchOffsetInDip || !zoneTransform || !selectedZone) return;

			// Get DIP Coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(selectedZone.spread, undefined);
			const pointerInDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;
			if (!pointerInDip) return;

			// Displacement in DIP for drag start
			const displacementInDip = pointerInDip
				.subVector(touchOffsetInDip)
				.subPoint(zoneTransform.getPosition());

			updateWithDisplacement(
				eXpress.math.vector(displacementInDip.left, displacementInDip.top),
			);
		},
		[getScreenToSpreadTransform, updateWithDisplacement],
	);

	const end = useCallback(
		function endZoneMoving() {
			if (zoneMoveRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.makeLastNonInteractive();
				googleAnalytics.trackEventToGa({
					trackingAction: "ZoneMoved",
				});
			}
			useLines.setIsZoneMoving(false);
			useLines.setTransformType(TransformType.Move);
			zoneMovingState.actions.endZoneMove();
			zoneMoveRef.current = undefined;
		},
		[commandManager, googleAnalytics, useLines],
	);

	const cancel = useCallback(
		function cancelZoneMoving() {
			// Undo if last command is our move
			if (zoneMoveRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.undo(true);
			}
			zoneMovingState.actions.endZoneMove();
			useLines.setIsZoneMoving(false);
			useLines.setTransformType(TransformType.Move);
			zoneMoveRef.current = undefined;
		},
		[commandManager, useLines],
	);

	return [
		isZoneMoving,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				startFromCurrentTransform,
				update,
				updateWithDisplacement,
				end,
				cancel,
			}),
			[start, startFromCurrentTransform, update, updateWithDisplacement, end, cancel],
		),
	] as const;
}
